<script lang="ts">
  import { orgClose, orgWarn } from "@nrk/origo";
  import { getQueryClientContext } from "@tanstack/svelte-query";
  import { useMachine } from "@xstate/svelte";

  import { createEventDispatcher } from "svelte";
  import { getCurrentUser } from "services/api";
  import type { MimirSearchHit } from "services/searchHit";
  import DeleteImageListItem from "./DeleteImageListItem.svelte";
  import { deleteImagesMachine, type DeleteImageState } from "./deleteImagesMachine";

  const dispatch = createEventDispatcher<{ deleted: void }>();

  export let selectedItems: MimirSearchHit[] = [];
  const currentUserId = getCurrentUser().id;
  const deletableCount = selectedItems.filter((item) => item.createdBy === currentUserId).length;

  const { state, send } = useMachine(
    deleteImagesMachine.withContext({
      unpublishedItems: selectedItems.filter(
        (item) => item.createdBy === currentUserId && !item.metadata.publicId,
      ),
      publishedItems: selectedItems.filter(
        (item) => item.createdBy === currentUserId && item.metadata.publicId,
      ),
      deletedItems: [],
      hiddenItems: [],
      failedItems: [],
      errorMessages: {},
      queryClient: getQueryClientContext(),
    }),
  );

  function dialogText(state: DeleteImageState) {
    const manyDeletable = deletableCount > 1;
    const manyPublished = state.context.publishedItems.length > 1;

    switch (state.value) {
      case "Confirming":
        return manyDeletable
          ? `Du er i ferd med å slette ${deletableCount} bilder fra Bildebanken, denne handlingen kan ikke angres:`
          : `Du er i ferd med å slette ett bilde fra Bildebanken, denne handlingen kan ikke angres:`;
      case "ConfirmingPublishedImages":
        return manyPublished
          ? "Disse bildene er publisert, og hvis du sletter dem vil de også forsvinne fra NRK.no, NRK TV eller NRK Radio dersom de er i bruk. <br /> <br/> Du kan også velge å skjule bildene ved å gjøre dem synlige kun for din bruker. Da vil de fortsatt vises for publikum der de evt. er publisert,  men ikke lenger være synlig i Bildebanken for andre brukere."
          : "Dette bildet er publisert, og hvis du sletter det vil det også forsvinne fra NRK.no, NRK TV eller NRK Radio dersom det er i bruk. <br /> <br/> Du kan også velge å skjule bildet ved å gjøre det synlig kun for din bruker. Da vil det fortsatt vises for publikum der det evt. er publisert,  men ikke lenger være synlig i Bildebanken for andre brukere.";
      case "DeletingUnpublishedImages":
      case "DeletingPublishedImages":
        return "Sletter…";
      case "HidingPublishedImages":
        return manyPublished ? "Skjuler bilder…" : "Skjuler bilde…";
      case "Failure":
        return "Sletting feilet";
      case "Success":
        return "";
      case "Cancelled":
        return "Sletting avbrutt";
      default:
        return "";
    }
  }

  function onCancel() {
    if (!$state.done) {
      send({ type: "CANCEL" });
    }
  }

  $: if ($state.matches("Success") && $state.context.deletedItems.length > 0) {
    dispatch("deleted");
  }

  const skippedItems = selectedItems.filter((item) => item.createdBy !== currentUserId);
</script>

<div class="wrapper">
  <header class="org-bar org-bar--primary" class:org-color-error={$state.matches("Failure")}>
    {#if $state.matches("Failure")}
      <h1>Sletting feilet</h1>
    {:else if $state.matches("ConfirmingPublishedImages")}
      <h1>Bekreft sletting</h1>
    {:else}
      <h1>Slett bilder</h1>
    {/if}
    <button class="org-button close-button" on:click={onCancel} data-for="close">
      {@html orgClose}
      <span class="org-sr">Lukk dialogen</span>
    </button>
  </header>
  <div class="message">
    {#if deletableCount >= 1}
      <p>{@html dialogText($state)}</p>
    {/if}
    {#if $state.matches("Confirming")}
      <ul>
        {#each [...$state.context.unpublishedItems, ...$state.context.publishedItems] as item}
          <DeleteImageListItem {item} />
        {/each}
      </ul>
    {:else if $state.matches("ConfirmingPublishedImages")}
      <ul>
        {#each $state.context.publishedItems as item}
          <DeleteImageListItem {item} />
        {/each}
      </ul>
    {:else}
      {#if $state.context.deletedItems.length > 0}
        <p>Disse bildene ble slettet:</p>
        <ul>
          {#each $state.context.deletedItems as deletedItem}
            <DeleteImageListItem item={deletedItem} disabled={true} />
          {/each}
        </ul>
      {/if}
      {#if $state.context.hiddenItems.length > 0}
        <p>Disse bildene ble skjult:</p>
        <ul>
          {#each $state.context.hiddenItems as hiddenItem}
            <DeleteImageListItem item={hiddenItem} disabled={true} />
          {/each}
        </ul>
      {/if}
      {#if $state.context.failedItems.length > 0}
        <p>Disse bildene feilet ved sletting:</p>
        <ul>
          {#each $state.context.failedItems as failedItem}
            <span>
              {@html orgWarn}
              {#if $state.context.errorMessages[failedItem.id]}
                <span class="error-message">{$state.context.errorMessages[failedItem.id]}</span>
              {/if}
            </span>
          {/each}
        </ul>
      {/if}
    {/if}
    {#if skippedItems.length > 0 && $state.matches("Confirming")}
      <p class="disabled">Bildene under kan ikke slettes fordi de tilhører andre brukere:</p>
      <ul>
        {#each skippedItems as skippedItem}
          <DeleteImageListItem item={skippedItem} disabled={true} />
        {/each}
      </ul>
    {/if}
  </div>
  <div class="dialog-toolbar">
    {#if $state.matches("Confirming")}
      <button class="org-button org-button--secondary" data-for="close"> Avbryt </button>
      <button
        class="org-button org-color-error"
        data-testid="delete-image-confirm"
        aria-disabled={!deletableCount}
        on:click={() => send("CONFIRM_DELETE")}
      >
        Slett {$state.context.publishedItems.length + $state.context.unpublishedItems.length > 1
          ? "bilder"
          : "bilde"}
      </button>
    {:else if $state.matches("ConfirmingPublishedImages")}
      <button class="org-button" on:click={onCancel} data-for="close"> Avbryt </button>
      <button
        class="org-button org-button--secondary"
        data-testid="hide-image-confirm"
        aria-disabled={!deletableCount}
        on:click={() => send("CONFIRM_HIDE")}
      >
        Gjør kun synlig for meg
      </button>
      <button
        class="org-button org-color-error"
        data-testid="delete-image-confirm-for-published-images"
        aria-disabled={!deletableCount}
        on:click={() => send("CONFIRM_DELETE")}
      >
        Slett {$state.context.publishedItems.length > 1 ? "bilder" : "bilde"}
      </button>
    {:else if $state.matches("DeletingUnpublishedImages") || $state.matches("DeletingPublishedImages") || $state.matches("HidingPublishedImages")}
      <button class="org-button org-button--primary" on:click={onCancel} data-for="close">
        Avbryt
      </button>
    {:else}
      <button class="org-button org-button--primary" on:click={onCancel} data-for="close">
        Lukk
      </button>
    {/if}
  </div>
</div>

<style>
  .wrapper {
    min-height: 350px;
    max-height: 60vh;
    display: flex;
    flex-direction: column;
    gap: var(--org-small);
  }

  header {
    flex-shrink: 0;
  }

  .message {
    flex: 1;
    overflow-y: auto;
    padding: 0 var(--org-medium);
  }

  .dialog-toolbar {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    padding: var(--org-small);
    gap: var(--org-xsmall);
  }

  .close-button {
    position: absolute;
    top: 5px;
    right: 5px;
  }

  button.org-color-error:hover {
    background-color: var(--org-color-error);
    filter: brightness(1.1);
  }

  p {
    margin: var(--org-small) 0;
  }

  ul {
    margin: var(--org-medium) 0;
    padding: 0 var(--org-small);
  }

  .error-message {
    margin-left: var(--org-xsmall);
  }

  .disabled {
    opacity: 0.5;
  }
</style>
